import React, { useState, useEffect } from 'react';

function AIConfigurationModal({ configuration, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    api_type: 'claude',
    api_url: '',
    api_key: '',
    model_name: '',
    prompt_template: '',
    max_tokens: 4000,
    temperature: 0.7,
    is_active: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (configuration) {
      setFormData({
        name: configuration.name || '',
        api_type: configuration.api_type || 'claude',
        api_url: configuration.api_url || '',
        api_key: '', // Always start empty for security
        model_name: configuration.model_name || '',
        prompt_template: configuration.prompt_template || '',
        max_tokens: parseInt(configuration.max_tokens) || 4000,
        temperature: parseFloat(configuration.temperature) || 0.7,
        is_active: Boolean(configuration.is_active)
      });
    } else {
      // Set defaults for new configuration
      setFormData({
        name: '',
        api_type: 'claude',
        api_url: 'https://api.anthropic.com/v1/messages',
        api_key: '',
        model_name: 'claude-3-5-sonnet-20241022',
        prompt_template: getDefaultPromptTemplate('claude'),
        max_tokens: 4000,
        temperature: 0.7,
        is_active: false
      });
    }
  }, [configuration]);

  const getDefaultPromptTemplate = (apiType) => {
    if (apiType === 'claude') {
      return `Please analyze this image and extract all items/products listed. This is for inventory management in a "{inventory_name}" inventory.

For each item found, please provide the following fields:
{field_descriptions}
{location_info}

Instructions:
1. Look for any lists, tables, invoices, catalogs, or handwritten notes containing items
2. Extract as much information as possible for each field
3. For fields with specific options, only use the provided options
4. If information is not clearly visible, leave the field empty rather than guessing
5. Be thorough - capture all items you can identify
6. For quantities, if you see "x2" or "2x", create separate items for each unit

Please respond with a JSON array where each object represents one item with field_values containing the extracted data.

Example format:
[
  {
    "field_values": {
      "Product Name": "Example Item",
      "Price": "19.99",
      "Category": "Electronics"
    }
  }
]

Respond only with the JSON array, no additional text.`;
    } else if (apiType === 'openai') {
      return `Analyze this image and extract all items/products for inventory management in "{inventory_name}".

Extract data for these fields:
{field_descriptions}
{location_info}

Return only a JSON array with extracted items in this format:
[{"field_values": {"field_name": "value"}}]`;
    } else {
      return `Extract items from this image for "{inventory_name}" inventory.

Fields needed:
{field_descriptions}
{location_info}

Return JSON array: [{"field_values": {"field_name": "value"}}]`;
    }
  };

  const getDefaultApiUrl = (apiType) => {
    switch (apiType) {
      case 'claude':
        return 'https://api.anthropic.com/v1/messages';
      case 'openai':
        return 'https://api.openai.com/v1/chat/completions';
      default:
        return '';
    }
  };

  const getDefaultModelName = (apiType) => {
    switch (apiType) {
      case 'claude':
        return 'claude-3-5-sonnet-20241022';
      case 'openai':
        return 'gpt-4o';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleApiTypeChange = (e) => {
    const newApiType = e.target.value;
    setFormData(prev => ({
      ...prev,
      api_type: newApiType,
      api_url: getDefaultApiUrl(newApiType),
      model_name: getDefaultModelName(newApiType),
      prompt_template: getDefaultPromptTemplate(newApiType)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.api_url.trim()) {
      newErrors.api_url = 'API URL is required';
    }

    // API key is required for new configurations, but optional for edits (empty means keep current)
    if (!formData.api_key.trim() && formData.api_type !== 'custom' && !configuration) {
      newErrors.api_key = 'API key is required for this service';
    }

    if (formData.max_tokens < 100 || formData.max_tokens > 8000) {
      newErrors.max_tokens = 'Max tokens must be between 100 and 8000';
    }

    if (formData.temperature < 0 || formData.temperature > 2) {
      newErrors.temperature = 'Temperature must be between 0 and 2';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2>{configuration ? 'Edit AI Configuration' : 'Add AI Configuration'}</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Configuration Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., My Claude Setup"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="api_type">API Type *</label>
            <select
              id="api_type"
              name="api_type"
              value={formData.api_type}
              onChange={handleApiTypeChange}
            >
              <option value="claude">Claude (Anthropic)</option>
              <option value="openai">OpenAI</option>
              <option value="custom">Custom API</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="api_url">API URL *</label>
            <input
              type="url"
              id="api_url"
              name="api_url"
              value={formData.api_url}
              onChange={handleInputChange}
              placeholder="https://api.example.com/v1/endpoint"
              className={errors.api_url ? 'error' : ''}
            />
            {errors.api_url && <span className="error-message">{errors.api_url}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="api_key">API Key {formData.api_type !== 'custom' && '*'}</label>
            <input
              type="password"
              id="api_key"
              name="api_key"
              value={formData.api_key}
              onChange={handleInputChange}
              placeholder={configuration && configuration.has_api_key ? "Enter new API key (leave empty to keep current)" : "Enter your API key"}
              className={errors.api_key ? 'error' : ''}
            />
            {configuration && configuration.has_api_key && (
              <small style={{ color: '#666', fontSize: '0.85em', display: 'block', marginTop: '0.25rem' }}>
                Current API key: {configuration.api_key_masked} (leave empty to keep current key)
              </small>
            )}
            {errors.api_key && <span className="error-message">{errors.api_key}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="model_name">Model Name</label>
            <input
              type="text"
              id="model_name"
              name="model_name"
              value={formData.model_name}
              onChange={handleInputChange}
              placeholder="e.g., claude-3-5-sonnet-20241022"
            />
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1, marginRight: '1rem' }}>
              <label htmlFor="max_tokens">Max Tokens</label>
              <input
                type="number"
                id="max_tokens"
                name="max_tokens"
                value={formData.max_tokens}
                onChange={handleInputChange}
                min="100"
                max="8000"
                className={errors.max_tokens ? 'error' : ''}
              />
              {errors.max_tokens && <span className="error-message">{errors.max_tokens}</span>}
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="temperature">Temperature</label>
              <input
                type="number"
                id="temperature"
                name="temperature"
                value={formData.temperature}
                onChange={handleInputChange}
                min="0"
                max="2"
                step="0.1"
                className={errors.temperature ? 'error' : ''}
              />
              {errors.temperature && <span className="error-message">{errors.temperature}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="prompt_template">Prompt Template</label>
            <textarea
              id="prompt_template"
              name="prompt_template"
              value={formData.prompt_template}
              onChange={handleInputChange}
              rows="8"
              placeholder="Custom prompt template with {inventory_name}, {field_descriptions}, and {location_info} placeholders"
              style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
            />
            <small style={{ color: '#666', fontSize: '0.85em' }}>
              Available placeholders: {'{inventory_name}'}, {'{field_descriptions}'}, {'{location_info}'}
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              Set as active configuration
            </label>
            <small style={{ color: '#666', fontSize: '0.85em', display: 'block', marginTop: '0.25rem' }}>
              Only one configuration can be active at a time. This will be used for image import.
            </small>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn">
              {configuration ? 'Update Configuration' : 'Add Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AIConfigurationModal;